import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import P11AdminLayout from "./P11AdminLayout";
import { useP11Role } from "./P11AdminGuard";
import { P11 } from "./p11shared";

// Deelnemerdetail: profielsecties + acties (cancel/remove/promote/invite/release/transfer).
export default function P11AdminParticipantDetail() {
  const { participantId } = useParams();
  const nav = useNavigate();
  const role = useP11Role();
  const [p, setP] = useState(null);
  const [confirm, setConfirm] = useState(null); // "cancel" | "remove" | "promote" | "release"
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/project11/admin/participants/${participantId}`).then(({ data }) => setP(data)).catch(() => setP(false));
  useEffect(() => { load(); }, [participantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (action) => {
    setBusy(true);
    try {
      if (action === "cancel") await api.post(`/project11/admin/participants/${participantId}/cancel`, { release_number: true });
      if (action === "remove") await api.delete(`/project11/admin/participants/${participantId}`);
      if (action === "promote") await api.post(`/project11/admin/participants/${participantId}/promote`);
      if (action === "invite") await api.post(`/project11/admin/participants/${participantId}/invite`);
      if (action === "release") await api.post(`/project11/admin/numbers/release`, { participant_id: participantId });
      toast.success("Uitgevoerd");
      setConfirm(null);
      if (action === "remove") nav("/project11/admin/participants");
      else load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Actie mislukt");
    } finally { setBusy(false); }
  };

  if (p === null) return <P11AdminLayout><span className="p11-label">…</span></P11AdminLayout>;
  if (p === false) return <P11AdminLayout><span className="p11-label" data-testid="p11-detail-missing">Deelnemer niet gevonden.</span></P11AdminLayout>;
  const enr = p.enrollment || {};
  const sessions = (p.attendance || []).length;
  const verified = (p.attendance || []).filter((a) => a.verified).length;

  const ActionBtn = ({ action, label, danger = false, testid }) => (
    <button onClick={() => setConfirm(action)} className="p11-btn-ghost" style={danger ? { borderColor: "#7a2d2d", color: "#e08585" } : {}} data-testid={testid}>{label}</button>
  );

  return (
    <P11AdminLayout>
      <div data-testid="p11-admin-participant-detail">
        <button onClick={() => nav("/project11/admin/participants")} className="p11-label hover:text-white mb-6" data-testid="p11-detail-back">← PARTICIPANTS</button>
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="p11-label-yellow">{enr.status} · {enr.type || "—"}</div>
            <h1 className="p11-h1 text-3xl mt-3" data-testid="p11-detail-name">{p.name}.</h1>
            <div className="p11-mono text-2xl mt-3" style={{ color: P11.yellow }} data-testid="p11-detail-number">{p.number_display || "———"}</div>
          </div>
          {role === "owner" && (
            <div className="flex flex-wrap gap-2">
              {enr.status === "WAITLISTED" && <button onClick={() => setConfirm("promote")} className="p11-btn" data-testid="p11-detail-promote">PROMOTE →</button>}
              <ActionBtn action="invite" label="STUUR UITNODIGING" testid="p11-detail-invite" />
              {p.number && <ActionBtn action="release" label="RELEASE NUMBER" testid="p11-detail-release" />}
              {enr.status !== "CANCELLED" && <ActionBtn action="cancel" label="CANCEL ENROLLMENT" danger testid="p11-detail-cancel" />}
              <ActionBtn action="remove" label="REMOVE FROM PROJECT" danger testid="p11-detail-remove" />
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <section className="border p-6" style={{ borderColor: P11.border }} data-testid="p11-detail-identity">
            <div className="p11-label mb-4">// IDENTITY & CONTACT</div>
            <div className="space-y-2 text-sm" style={{ color: P11.gray2 }}>
              <div>{p.email}</div><div>{p.phone || "—"}</div><div>DOB: {p.dob}</div>
              <div>{[p.address?.street, p.address?.house_number, p.address?.postal_code, p.address?.city].filter(Boolean).join(" ") || "—"}</div>
              <div className="p11-label pt-2">ACCOUNT: {p.account?.account_state}</div>
            </div>
          </section>
          <section className="border p-6" style={{ borderColor: P11.border }} data-testid="p11-detail-training">
            <div className="p11-label mb-4">// TRAINING & GOALS</div>
            <div className="space-y-2 text-sm" style={{ color: P11.gray2 }}>
              <div>Ervaren: {p.experience ? "ja" : "nee"}</div>
              <div>Doelen: {(p.goals || []).join(", ") || "—"}{p.goals_other ? ` (${p.goals_other})` : ""}</div>
              <div>Notities: {p.extra_notes || "—"}</div>
            </div>
          </section>
          {role !== "viewer" && (
            <section className="border p-6" style={{ borderColor: P11.border }} data-testid="p11-detail-health">
              <div className="p11-label mb-4">// GEZONDHEID & NOODCONTACT</div>
              <div className="space-y-2 text-sm" style={{ color: P11.gray2 }}>
                <div>Blessures: {p.injuries ? p.injuries_text || "ja" : "nee"}</div>
                {p.emergency_contact?.name && <div>Noodcontact: {p.emergency_contact.name} ({p.emergency_contact.relation}) · {p.emergency_contact.phone}</div>}
              </div>
            </section>
          )}
          <section className="border p-6" style={{ borderColor: P11.border }} data-testid="p11-detail-progress">
            <div className="p11-label mb-4">// SESSIONS & STAMPS</div>
            <div className="p11-mono text-3xl" style={{ color: P11.yellow }}>{String(verified).padStart(2, "0")}/08</div>
            <div className="p11-label mt-2">{sessions} attendance records · {enr.completion_status || "IN PROGRESS"}</div>
            <div className="space-y-1 mt-4">
              {(p.attendance || []).sort((a, b) => a.session_id.localeCompare(b.session_id)).map((a) => (
                <div key={a.session_id} className="flex justify-between p11-label" style={{ color: P11.gray2 }}>
                  <span>{a.session_id.split("-").pop()}</span>
                  <span>{a.verified ? "VERIFIED" : a.status.toUpperCase()}</span>
                </div>
              ))}
            </div>
          </section>
          <section className="border p-6" style={{ borderColor: P11.border }} data-testid="p11-detail-consents">
            <div className="p11-label mb-4">// CONSENTS</div>
            <div className="space-y-1">
              {(p.consents || []).map((c) => (
                <div key={c.key} className="flex justify-between p11-label" style={{ color: P11.gray2 }}>
                  <span>{c.key}</span><span style={{ color: c.accepted ? P11.yellow : P11.gray }}>{c.accepted ? "JA" : "NEE"}</span>
                </div>
              ))}
              {!(p.consents || []).length && <div className="p11-label" style={{ color: P11.gray }}>Nog niet afgerond door deelnemer</div>}
            </div>
          </section>
          <section className="border p-6" style={{ borderColor: P11.border }} data-testid="p11-detail-number-history">
            <div className="p11-label mb-4">// NUMBER HISTORY</div>
            <div className="space-y-1">
              {(p.number_history || []).map((h) => (
                <div key={h.id} className="p11-label" style={{ color: P11.gray2 }}>
                  #{String(h.number).padStart(3, "0")} · {h.status} · {h.assigned_at?.slice(0, 10)}{h.released_at ? ` → ${h.released_at.slice(0, 10)}` : ""}
                </div>
              ))}
              {!(p.number_history || []).length && <div className="p11-label" style={{ color: P11.gray }}>Geen toewijzingen</div>}
            </div>
          </section>
        </div>

        {confirm && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setConfirm(null)}>
            <div className="bg-[#080808] border max-w-sm w-full p-8" style={{ borderColor: P11.border }} onClick={(e) => e.stopPropagation()} data-testid="p11-detail-confirm">
              <div className="p11-label-yellow mb-4">// BEVESTIG ACTIE</div>
              <p className="text-sm" style={{ color: P11.gray2 }}>
                {confirm === "cancel" && "Inschrijving annuleren en nummer vrijgeven? Capaciteit komt vrij."}
                {confirm === "remove" && "Deelnemer volledig verwijderen uit het project? Persoonsgegevens, aanwezigheid en nummer worden verwijderd/vrijgegeven. Dit kan niet ongedaan worden."}
                {confirm === "promote" && "Wachtlijst-deelnemer promoveren naar bevestigde plek? Er wordt direct een nummer toegewezen."}
                {confirm === "release" && "Nummer vrijgeven voor hergebruik? De deelnemer blijft ingeschreven zonder nummer."}
                {confirm === "invite" && "Afrond-uitnodiging opnieuw versturen?"}
              </p>
              <div className="flex justify-end gap-3 mt-6">
                <button onClick={() => setConfirm(null)} className="p11-btn-ghost" data-testid="p11-detail-confirm-no">ANNULEREN</button>
                <button onClick={() => act(confirm)} disabled={busy} className="p11-btn" data-testid="p11-detail-confirm-yes">{busy ? "…" : "BEVESTIGEN"}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </P11AdminLayout>
  );
}
